import { getChartLayout, postChartLayout } from "api/dashboardAPI";
import {
  DashbaordAlertSendMessage,
  DashboarAlertLatest,
} from "api/interfaces/dashboardInterface.interface";
import { useCallback, useEffect, useState } from "react";
import { Layout } from "react-grid-layout";
import { useQuery } from "react-query";

import WidgetPresenter from "./WidgetPresenter";
import { useProSidebar } from "react-pro-sidebar";
import {
  GetWidgetRecorderResponse,
  StatResultAccountData,
  WidgetCardData,
  WidgetStats,
} from "api/interfaces/widgetInterface.interface";
import {
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  getWidgetCountAccount,
  getWidgetCountAlert,
  getWidgetCountCamera,
  getWidgetCountRecorder,
} from "api/widgetAPI";
import { CardItemName } from "./item/card/CardItem";
import { Datum } from "@nivo/line";
import { isCheckPermission } from "utils/AccountUtil";
import useStatusMutation from "hook/useStatusMutation";
import {
  AssignModalType,
  cardInitialLayout,
  changeStatsToLine,
  chartInitialLayout,
  initialCardData,
  TableItemName,
  tableItemPermission,
  deduplicateLayoutByKey,
  compactLayout,
} from "utils/WidgetUtil";
import { ColumnText, OrgText } from "./item/table/TableItemStyled";
import { getLevelFullName } from "api/mappers/accountMapper";
import { Level } from "components/atoms/level/Level";
import { ColumnType } from "antd/es/table";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

// organization common column
export const commonOrgColumn = (
  selectedAccount: ProfileAccountInfo
): ColumnType<any> => {
  return {
    title: "Organization",
    key: "accountName",
    dataIndex: "accountName",
    hidden: selectedAccount.accountLevel === LEVEL_TYPE.EU,
    render: (value) => (
      <ColumnText>
        <Level level={getLevelFullName(LEVEL_TYPE.EU)} />
        <OrgText>{value}</OrgText>
      </ColumnText>
    ),
    width: 100,
  };
};
export default function WidgetContainer(): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { collapsed } = useProSidebar();

  const [cardLayout, setCardLayout] = useState<Layout[] | undefined>();
  const [chartLayout, setChartLayout] = useState<Layout[] | undefined>();

  const [isAddModal, setIsAddModal] = useState<boolean>(false);

  const [isDetailView, setIsDetailView] = useState<CardItemName | null>(null);

  const [rowHeight, setRowHeight] = useState<number>(150);

  const [cardData, setCardData] = useState<WidgetCardData>({
    recorderCount: { ...initialCardData },
    cameraCount: { ...initialCardData },
    alertCount: { ...initialCardData },
    organizationCount: { ...initialCardData },
  });

  // Alerts Widget - assign mdoal
  const [isAssignModal, setIsAssignModal] = useState<AssignModalType>({
    phone: false,
    email: false,
  });

  const [assignInfo, setAssignInfo] = useState<{ [key: string]: string }>({
    phone: "",
    email: "",
  });

  const [selectedAlertItem, setSelectedAlertItem] =
    useState<DashbaordAlertSendMessage>({
      alertRuleId: "",
      recorderId: "",
    });

  const onAssignModal = (type: string, value?: string) => {
    if (value !== undefined) {
      setAssignInfo({
        ...assignInfo,
        [type]: value,
      });
    }

    setIsAssignModal({
      ...isAssignModal,
      [type]: !isAssignModal[type],
    });
  };

  const onSelectItem = (item: DashboarAlertLatest) => {
    setSelectedAlertItem({
      alertRuleId: item.alertRuleId,
      recorderId: item.recorderId,
    });
  };

  // 창 크기에 따라 widget 높이를 다르게 설정해야 함.
  const changeRowHeight = () => {
    if (window.innerWidth >= 1800) {
      setRowHeight(200);
    } else {
      setRowHeight(150);
    }
  };

  useEffect(() => {
    changeRowHeight();
    window.addEventListener("resize", changeRowHeight);
    return () => {
      window.removeEventListener("resize", changeRowHeight);
    };
  }, []);

  // side bar collapse 할 때 size 조절
  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 200);
  }, [collapsed]);

  const calEmptySpace = (layout: Layout[]) => {
    // 각 열에서의 최대 y 값을 담는 배열 초기화
    const colHeights = new Array(2).fill(0);

    // 각 열의 가장 큰 y 값 계산
    layout.forEach((lay) => {
      const bottomY = lay.y + lay.h;
      colHeights[lay.x] = Math.max(colHeights[lay.x], bottomY);
    });

    return colHeights;
  };

  const { isLoading, refetch } = useQuery(
    ["dashboardLayout", selectedAccount.accountId],
    () => getChartLayout(selectedAccount.accountId),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.result !== undefined) {
          const cardReturn = (d: Layout) => {
            if (
              (!selectedAccount.userPermission.viewAlerts &&
                d.i === "alertCount") ||
              (selectedAccount.accountLevel === "EU" &&
                d.i === "organizationCount")
            ) {
              return false;
            }
            return true;
          };
          let tempCardLayout = res.result.filter((d: Layout) => {
            if (!selectedAccount.isEnabled && d.i === "alertCount") {
              return false;
            }
            if (!cardReturn(d)) {
              return false;
            }
            return d.i.includes("Count");
          });
          tempCardLayout = deduplicateLayoutByKey(tempCardLayout);
          let tempChartLayout = res.result
            .filter((d: Layout) => {
              if (d.i.includes("Count")) return false;

              if (tableItemPermission[d.i as TableItemName] === "rw")
                return true;

              const permission = tableItemPermission[d.i as TableItemName]?.[0];
              if (permission) {
                return isCheckPermission(permission, selectedAccount);
              }
              if (!cardReturn(d)) {
                return false;
              }
              return true;
            })
            .sort((a: Layout, b: Layout) => {
              if (a.y === b.y) {
                return a.x - b.x;
              }
              return a.y - b.y;
            });

          tempChartLayout = deduplicateLayoutByKey(tempChartLayout);

          setCardLayout(compactLayout(tempCardLayout, 4));
          setChartLayout(compactLayout(tempChartLayout, 2));
        } else {
          setCardLayout(cardInitialLayout);
          setChartLayout(chartInitialLayout);
        }
      },
      onError: (e: any) => {
        setCardLayout(cardInitialLayout);
        setChartLayout(chartInitialLayout);
      },
    }
  );

  const mutationUpdateLayout = useStatusMutation(postChartLayout, {
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      console.log("erre");
    },
    onSettled: () => {},
  });
  // ----------- Card QUERY
  // const getCardRecorder =
  const { refetch: refetchRecorder } = useQuery(
    ["getWidgetCountRecorder", selectedAccount.accountId],
    () => getWidgetCountRecorder({ accountId: selectedAccount.accountId }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetWidgetRecorderResponse) => {
        if (res.result) {
          // console.log(res.result, "getWidgetCountRecorder");
          setCardData((cur) => {
            return {
              ...cur,
              recorderCount: {
                ...res.result,
                stats: changeStatsToLine(res.result.stats as WidgetStats[]),
              },
            };
          });
        } else {
          setCardData((cur) => {
            return {
              ...cur,
              recorderCount: { ...initialCardData },
            };
          });
        }
      },
      onError: (e: any) => {
        console.log(e, "error");
        setCardData((cur) => {
          return {
            ...cur,
            recorderCount: { ...initialCardData },
          };
        });
      },
    }
  );
  // const getCardCamera =
  const { refetch: refetchCamera } = useQuery(
    ["getWidgetCountCamera", selectedAccount.accountId],
    () => getWidgetCountCamera({ accountId: selectedAccount.accountId }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetWidgetRecorderResponse) => {
        if (res.result) {
          // console.log(res.result, "getWidgetCountCamera");
          setCardData((cur) => {
            return {
              ...cur,
              cameraCount: {
                ...res.result,
                stats: changeStatsToLine(res.result.stats as WidgetStats[]),
              },
            };
          });
        } else {
          setCardData((cur) => {
            return {
              ...cur,
              cameraCount: { ...initialCardData },
            };
          });
        }
      },
      onError: (e: any) => {
        console.log(e, "error");
        setCardData((cur) => {
          return {
            ...cur,
            cameraCount: { ...initialCardData },
          };
        });
      },
    }
  );

  const refecth = useCallback(() => {
    refetchCamera();
    refetchRecorder();
  }, [refetchCamera, refetchRecorder]);
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refecth,
  });
  // const getCardAlert =
  const { refetch: refetchAlert } = useQuery(
    ["getWidgetCountAlert", selectedAccount.accountId],
    () => getWidgetCountAlert({ accountId: selectedAccount.accountId }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetWidgetRecorderResponse) => {
        if (res.result && selectedAccount.isEnabled) {
          const sortOrder: Record<string, number> = {
            System: 0,
            Disk: 1,
            Video: 2,
          };
          setCardData((cur) => {
            return {
              ...cur,
              alertCount: {
                ...res.result,
                details: res.result.details.sort((a, b) => {
                  return (sortOrder[a.type] ?? 99) - (sortOrder[b.type] ?? 99);
                }),
                stats: changeStatsToLine(res.result.stats as WidgetStats[]),
              },
            };
          });
        } else {
          setCardData((cur) => {
            return {
              ...cur,
              alertCount: {
                ...initialCardData,
                details: ["System", "Disk", "Video"].map((type) => ({
                  type: type,
                  count: 0,
                })),
              },
            };
          });
        }
      },
      onError: (e: any) => {
        console.log(e, "error");
        setCardData((cur) => {
          return {
            ...cur,
            alertCount: { ...initialCardData },
          };
        });
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["alertListUpdated"] as StompDateSignalKey[],
    refetch: refetchAlert,
  });
  // const getCardAccount =
  const { refetch: refetchAccount } = useQuery(
    ["getWidgetCountAccount", selectedAccount.accountId],
    () => getWidgetCountAccount({ accountId: selectedAccount.accountId }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetWidgetRecorderResponse) => {
        if (res.result) {
          // console.log(res.result, "getWidgetCountAccount");
          setCardData((cur) => {
            let tempStats: Datum[] = res.result.stats
              ? (res.result.stats as StatResultAccountData[]).map(
                  (stat: StatResultAccountData) => {
                    return {
                      x: stat.date,
                      y: stat.cpCount + stat.euCount,
                    };
                  }
                )
              : [];
            return {
              ...cur,
              organizationCount: {
                ...res.result,
                stats: tempStats,
              },
            };
          });
        } else {
          setCardData((cur) => {
            return {
              ...cur,
              organizationCount: { ...initialCardData },
            };
          });
        }
      },
      onError: (e: any) => {
        console.log(e, "error");
        setCardData((cur) => {
          return {
            ...cur,
            organizationCount: { ...initialCardData },
          };
        });
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["organizationListUpdated"] as StompDateSignalKey[],
    refetch: refetchAccount,
  });
  const onUpdateLayout = useCallback(
    (
      card: Layout[] | undefined,
      chart: Layout[] | undefined,
      options?: { skipCompact?: boolean }
    ) => {
      if (!selectedAccount.isEnabled) return;
      if (!mutationUpdateLayout.idle) return;
      if (card !== undefined && chart !== undefined) {
        const tempLayouts =
          options?.skipCompact === true
            ? [...card, ...chart]
            : [...compactLayout(card, 4), ...compactLayout(chart, 2)];
        mutationUpdateLayout.mutate({
          accountId: selectedAccount.accountId,
          payload: tempLayouts,
        });
      }
    },
    [selectedAccount.accountId]
  );

  //위젯 추가시
  const onAddLayout = useCallback(
    (type?: string, newItems?: string[]) => {
      if (type === "close") {
        setIsAddModal(false);
        if (
          newItems !== undefined &&
          chartLayout !== undefined &&
          cardLayout !== undefined
        ) {
          let tmpLayout = chartLayout.filter((lay) => newItems.includes(lay.i));
          // 삭제 반영 후 빈 칸 제거: 기존 항목을 왼쪽 위부터 재배치
          tmpLayout = compactLayout(tmpLayout, 2);
          const existingIds = new Set(tmpLayout.map((l) => l.i));
          const toAdd = newItems.filter((i) => !existingIds.has(i));

          // 각 열의 최대 y 좌표 배열을 계산
          const colHeights = calEmptySpace(tmpLayout);
          toAdd.forEach((item) => {
            const minColIndex = colHeights[0] > colHeights[1] ? 1 : 0;
            const tmp: Layout = {
              i: item,
              x: minColIndex,
              y: colHeights[minColIndex],
              w: 1,
              h: 1,
              minW: 1,
              minH: 1,
            };
            colHeights[minColIndex] += tmp.h;
            tmpLayout.push(tmp);
          });

          onUpdateLayout(cardLayout, tmpLayout, { skipCompact: true });
        }
      } else {
        setIsAddModal(!isAddModal);
      }
    },
    [isAddModal, chartLayout, cardLayout, onUpdateLayout]
  );
  //상단카드 레이아웃 변경시
  const onCardLayoutChange = (layout: Layout[]) => {
    const updatedLayout: Layout[] = layout.map((item) => {
      return { ...item, y: 0 };
    });
    onUpdateLayout(updatedLayout, chartLayout);
  };

  //테이블 레이아웃 변경시
  const onTableLayoutChange = (layout: Layout[]) => {
    onUpdateLayout(cardLayout, layout);
  };

  //삭제시
  const onDeleteWidget = (key: TableItemName | TableItemName[]) => {
    const keysToRemove = Array.isArray(key) ? key : [key];
    let filterLayout = chartLayout?.filter(
      (lay) => !keysToRemove.includes(lay.i as TableItemName)
    );
    onUpdateLayout(cardLayout, filterLayout, { skipCompact: true });
  };

  const onViewDetailCard = (key: CardItemName) => {
    if (isDetailView === key) {
      setIsDetailView(null);
    } else {
      setIsDetailView(key);
    }
  };
  const [isDraggable, setIsDraggable] = useState<boolean>(false);

  return (
    <WidgetPresenter
      cardLayout={cardLayout}
      chartLayout={chartLayout}
      isAddModal={isAddModal}
      onAddLayout={onAddLayout}
      onCardLayoutChange={onCardLayoutChange}
      onTableLayoutChange={onTableLayoutChange}
      onDeleteWidget={onDeleteWidget}
      isLoading={isLoading}
      rowHeight={rowHeight}
      isAssignModal={isAssignModal}
      onAssignModal={onAssignModal}
      assignInfo={assignInfo}
      selectedAlertItem={selectedAlertItem}
      onSelectItem={onSelectItem}
      cardData={cardData}
      onViewDetailCard={onViewDetailCard}
      isDetailView={isDetailView}
      isEnabled={selectedAccount.isEnabled}
      onToggleDraggable={setIsDraggable}
      isDraggable={isDraggable}
    />
  );
}
